import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { database } from '../firebaseConfig';
import { ref, update, get } from 'firebase/database';
import { getTransaction, getTransactionForUser } from '../services/transactionService';
import { sendUnderContractEmail } from '../services/emailSend';
import { startDealCheckIns } from '../services/checkInService';
import { syncTaskDates, syncTeamTaskDates, syncExpirationReminders, startSellerUnderContract } from '../services/transactionTasks';
import { listDealDocuments, uploadDealDocument, deleteDealDocument } from '../services/dealDocuments';
import DocChecklistUpload, { uploadPendingDocs } from '../components/DocChecklistUpload';
import { docsFor, missingDocs, missingDocsMessage } from '../shared/transactionDocs';
import CelebrationOverlay from '../components/CelebrationOverlay';
import { deadlineFrom } from '../shared/deadlines';

// Sections appear in this order. Fields inside each section follow the order Kelly gave.
// `when` hides a field unless the condition is true. Hidden fields are not required and are not saved.
const SECTIONS = [
  {
    title: 'Contract & Deadlines',
    fields: [
      { key: 'transactionAmount', label: 'Transaction Amount', type: 'money', required: true },
      { key: 'underContractDate', label: 'Under Contract Date', hint: 'This is the Effective Date (day 0) for deadlines set in days.', type: 'date', required: true },
      { key: 'forecastedClosedDate', label: 'Anticipated Closing Date', type: 'date', days: true, required: true },
      { key: 'dueDiligenceDeadline', label: 'Due Diligence Deadline', type: 'date', days: true, required: true },
      { key: 'earnestMoneyDueDate', label: 'Earnest Money Due Date', type: 'date', days: true, required: true },
      { key: 'earnestMoneyAmount', label: 'Earnest Money Amount', type: 'money', required: true },
      { key: 'secondEmdDue', label: 'Second EMD Due Date', type: 'date', days: true },
      { key: 'secondEmdAmount', label: 'Second EMD Amount', type: 'money', hint: 'Numbers only, no $ sign' },
      { key: 'financingType', label: 'Financing Type', type: 'select', options: ['Cash', 'Mortgage'], required: true },
      { key: 'financingContingencyDeadline', label: 'Financing Contingency Deadline', type: 'date', days: true, required: true, when: (v) => v.financingType === 'Mortgage' },
      { key: 'lenderFirstName', label: "Buyer's Lender First Name (optional)", when: (v) => v.financingType === 'Mortgage' },
      { key: 'lenderLastName', label: "Buyer's Lender Last Name (optional)", when: (v) => v.financingType === 'Mortgage' },
      { key: 'lenderPhone', label: "Buyer's Lender Phone (optional)", type: 'tel', when: (v) => v.financingType === 'Mortgage' },
      { key: 'lenderEmail', label: "Buyer's Lender Email (optional)", type: 'email', when: (v) => v.financingType === 'Mortgage' },
      { key: 'homeSaleContingency', label: 'Is there a home sale contingency?', type: 'yesno', required: true },
      { key: 'homeSaleContingencyDate', label: 'Home Sale Contingency Date', type: 'date', days: true, required: true, when: (v) => v.homeSaleContingency === 'Yes' },
      { key: 'homeSaleAgentFirstName', label: "Listing agent for the buyer's home to sell: first name (optional)", when: (v) => v.homeSaleContingency === 'Yes' },
      { key: 'homeSaleAgentLastName', label: "Listing agent for the buyer's home to sell: last name (optional)", when: (v) => v.homeSaleContingency === 'Yes' },
      { key: 'homeSaleAgentPhone', label: "Listing agent phone (optional)", type: 'tel', when: (v) => v.homeSaleContingency === 'Yes' },
      { key: 'homeSaleAgentEmail', label: "Listing agent email (optional)", type: 'email', when: (v) => v.homeSaleContingency === 'Yes' },
    ],
  },
  {
    title: 'Title',
    fields: [
      { key: 'titleOfficer', label: 'Title Officer', type: 'officer', required: true },
      { key: 'titleCompanyName', label: 'Title Company Name', required: true, when: (v) => !!v.titleOfficer },
      { key: 'titleAgentFirstName', label: 'Title Agent First Name', required: true, when: (v) => !!v.titleOfficer },
      { key: 'titleAgentLastName', label: 'Title Agent Last Name', required: true, when: (v) => !!v.titleOfficer },
      { key: 'titleAgentEmail', label: 'Title Agent Email', type: 'email', required: true, when: (v) => !!v.titleOfficer },
      { key: 'titleAgentPhone', label: 'Title Agent Phone Number', type: 'tel', required: true, when: (v) => !!v.titleOfficer },
    ],
  },
  {
    title: 'Buyers & Cooperating Agent',
    fields: [
      { key: 'buyerNames', label: 'Buyer Names', required: true },
      { key: 'cooperativeAgentFirstName', label: 'Cooperative Agent First Name' },
      { key: 'cooperativeAgentLastName', label: 'Cooperative Agent Last Name' },
      { key: 'cooperatingAgentPhone', label: 'Cooperating Agent Phone', type: 'tel', required: true },
      { key: 'cooperatingAgentEmail', label: 'Cooperating Agent Email', type: 'email', required: true },
      { key: 'cooperatingTcName', label: 'Cooperating TC Name' },
      { key: 'cooperatingTcPhone', label: 'Cooperating TC Phone', type: 'tel' },
      { key: 'cooperatingTcEmail', label: 'Cooperating TC Email', type: 'email' },
    ],
  },
  {
    title: 'Closing',
    fields: [
      { key: 'walkThroughDate', label: 'Walk Through Date', type: 'date', days: true, required: true },
      { key: 'closingGiftsVendor', label: 'Closing Gifts Vendor' },
      { key: 'closedDate', label: 'Closed (Settlement) Date (optional)', type: 'date', hint: 'Leave blank until the deal actually closes. The Anticipated Closing Date above is only a forecast and never marks the deal closed.' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
];

// Saved title officers. Each id is stored on the transaction as titleOfficer.
// Selecting one fills the company and agent fields automatically. "Other" lets the user type them in.
const OTHER_TITLE = 'other';

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields);
const ALL_DAY_FIELDS = ALL_FIELDS.filter((f) => f.days).map((f) => f.key);
const isVisible = (f, values) => !f.when || f.when(values);
const isMissing = (f, values) => isVisible(f, values) && f.required && !String(values[f.key] || '').trim();

const page = { minHeight: '100vh', background: '#000000', padding: '16px', fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' };
const panel = { background: '#ffffff', borderRadius: '10px', maxWidth: 560, margin: '0 auto', padding: '24px 20px', boxSizing: 'border-box' };
const label = { display: 'block', fontSize: '14px', fontWeight: 600, color: '#1a1a1a', marginBottom: '6px' };
const input = { width: '100%', boxSizing: 'border-box', padding: '12px', fontSize: '16px', border: '1px solid #d8c9a8', borderRadius: '6px', background: '#ffffff', color: '#1a1a1a', marginBottom: '4px' };
const hint = { fontSize: '12px', color: '#7f7f7f', marginBottom: '14px' };
const errorText = { fontSize: '12px', color: '#b00020', marginBottom: '14px' };
const field = { marginBottom: '16px' };
const sectionWrap = { marginBottom: '28px' };
const sectionTitle = { fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8C6D1F', fontWeight: 700, borderBottom: '1px solid #d8c9a8', paddingBottom: '8px', marginBottom: '18px' };
const yesNoBtn = { flex: 1, padding: '12px', borderRadius: '6px', border: '1px solid #d8c9a8', background: '#ffffff', color: '#1a1a1a', fontWeight: 600, fontSize: '16px', cursor: 'pointer' };
const yesNoBtnActive = { background: '#000000', color: '#ffffff', borderColor: '#000000' };
const backBtn = { background: 'none', border: 'none', color: '#8C6D1F', cursor: 'pointer', padding: 0, fontSize: '14px', marginBottom: '12px' };
const primaryBtn = { width: '100%', padding: '14px', background: '#000000', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '16px', cursor: 'pointer' };

export default function UnderContractForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  // The deal may belong to another agent (admin, TC or co-agent working it): read and write under its owner.
  const [ownerUid, setOwnerUid] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [values, setValues] = useState({});
  const [address, setAddress] = useState('');
  const [isFirstOpen, setIsFirstOpen] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [txType, setTxType] = useState('');
  const [pendingDocs, setPendingDocs] = useState({});
  const requiredDocs = docsFor(txType, 'underContract');

  // Title agents come from the Title Company partners.
  const [titleOfficers, setTitleOfficers] = useState([]);
  useEffect(() => {
    get(ref(database, 'partners')).then((snap) => {
      const all = snap.exists() ? snap.val() : {};
      setTitleOfficers(Object.entries(all)
        .map(([pid, p]) => ({
          id: pid,
          name: `${p.firstName || ''} ${p.lastName || ''}`.trim() + (p.companyName ? ` (${p.companyName})` : ''),
          company: p.companyName || '',
          firstName: p.firstName || '',
          lastName: p.lastName || '',
          email: p.email || '',
          phone: p.phone || '',
          partnerType: p.partnerType,
        }))
        .filter((p) => p.partnerType === 'Title Company'));
    }).catch(() => setTitleOfficers([]));
  }, []);

  useEffect(() => {
    if (!user?.uid || !id) return;
    (async () => {
      try {
        const tx = await getTransactionForUser(user.uid, id, userRole);
        if (tx) {
          const owner = tx.ownerUid || user.uid;
          setOwnerUid(owner);
          setTxType(tx.type || tx.transactionType || '');
          listDealDocuments(owner, id).then(setDocuments).catch((err) => console.error('Could not load documents', err));
          setAddress(tx.propertyAddress || tx.address || '');
          const base = tx.underContract || tx.underContractDraft || {};
          const ta = tx.titleAgent || {};
          // Carry the title agent picked on the buyer form into this form, if no title agent is set here yet.
          if (!base.titleOfficer && (ta.partnerId || ta.companyName || ta.email)) {
            const pick = ta.partnerId && ta.partnerId !== 'other' ? ta.partnerId : OTHER_TITLE;
            setValues({ ...base, titleOfficer: pick, titleCompanyName: ta.companyName || '', titleAgentFirstName: ta.firstName || '', titleAgentLastName: ta.lastName || '', titleAgentEmail: ta.email || '', titleAgentPhone: ta.phone || '' });
          } else {
            setValues(base);
          }
          // Celebrate only the first time this transaction's under contract form is opened.
          const firstOpen = !tx.underContract;
          setIsFirstOpen(firstOpen);
          // Celebrate once per workflow: not again after a save, and not again in this browser session.
          let alreadyCelebrated = false;
          try { alreadyCelebrated = sessionStorage.getItem(`rs-celebrated-${id}`) === '1'; } catch (e) { /* storage blocked */ }
          if (firstOpen && !alreadyCelebrated) setShowCelebration(true);
        }

      } catch (err) {
        console.error('Could not load transaction:', err);
        setError('Could not load this transaction.');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, id, userRole]);
  const owner = ownerUid || user?.uid;

  const missing = ALL_FIELDS.filter((f) => isMissing(f, values));
  const isMissingField = (f) => isMissing(f, values);

  // Deadline day-counting: each deadline can be set as N days from the Effective Date (Under Contract Date = day 0).
  // Stored as {key}Days and {key}DayMode next to the date; recomputed when the Effective Date changes.
  // Typing a date by hand clears the day count. Math: src/shared/deadlines.js (also used by the contract reader).
  const recomputeDeadlines = (v) => {
    const out = { ...v };
    ALL_DAY_FIELDS.forEach((k) => {
      if (out[`${k}Days`] === '' || out[`${k}Days`] == null) return;
      const r = deadlineFrom(out.underContractDate, out[`${k}Days`], out[`${k}DayMode`] || 'calendar');
      if (r) out[k] = r.date;
    });
    return out;
  };
  const change = (key, value) => setValues((v) => {
    if (key === 'underContractDate') return recomputeDeadlines({ ...v, underContractDate: value });
    if (ALL_DAY_FIELDS.includes(key)) return { ...v, [key]: value, [`${key}Days`]: '' };
    return { ...v, [key]: value };
  });
  const changeDays = (key, days, mode) => setValues((v) => recomputeDeadlines({ ...v, [`${key}Days`]: days, [`${key}DayMode`]: mode || v[`${key}DayMode`] || 'calendar' }));

  const uploadDocuments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of files) {
        await uploadDealDocument(owner, id, file, user.displayName || user.email || '');
      }
      setDocuments(await listDealDocuments(owner, id));
    } catch (err) {
      console.error('Could not upload document', err);
      setError(/storage\/(unknown|unauthorized|bucket-not-found|project-not-found)|404|403/i.test(`${err?.code || ''} ${err?.message || ''}`)
        ? 'The document did not upload: file storage is not set up for Resi yet. Your other answers are not affected. Ask Kelly to turn on Firebase Storage, or keep the file in SkySlope for now.'
        : 'Could not upload that document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = async (doc) => {
    try {
      await deleteDealDocument(owner, id, doc);
      setDocuments(await listDealDocuments(owner, id));
    } catch (err) {
      console.error('Could not remove document', err);
      setError('Could not remove that document.');
    }
  };

  const save = async () => {
    setShowErrors(true);
    setMsg(null);
    setError(null);
    if (missing.length > 0) {
      setError(`Please complete the ${missing.length} required field${missing.length === 1 ? '' : 's'} before saving.`);
      return;
    }
    const missingD = missingDocs(requiredDocs, pendingDocs, documents);
    if (missingD.length) { setError(missingDocsMessage(missingD)); return; }
    // Save only the fields that are currently shown, so hidden answers never linger.
    const payload = {};
    ALL_FIELDS.forEach((f) => {
      if (isVisible(f, values)) payload[f.key] = values[f.key] ?? '';
      if (f.days && isVisible(f, values) && values[`${f.key}Days`] !== undefined && values[`${f.key}Days`] !== '') {
        payload[`${f.key}Days`] = values[`${f.key}Days`];
        payload[`${f.key}DayMode`] = values[`${f.key}DayMode`] || 'calendar';
      }
    });
    try {
      setSaving(true);
      const current = await getTransaction(owner, id).catch(() => null);
      await uploadPendingDocs(owner, id, pendingDocs, user.displayName || user.email || '');
      setPendingDocs({});
      await update(ref(database, `users/${owner}/transactions/${id}`), {
        underContract: payload,
        underContractDraft: null,
        // Keep a closed (or lost) deal's status; only open deals become pending.
        ...(['closed', 'lost'].includes(current?.status) ? {} : { status: 'pending' }),
        updatedAt: new Date().toISOString(),
      });
      const fresh = await getTransaction(owner, id);
      await syncTaskDates(id, fresh);
      await syncExpirationReminders(id, fresh, user.uid);
      await startSellerUnderContract(id, { underContractDate: payload.underContractDate, closingDate: payload.forecastedClosedDate, dueDiligenceDeadline: payload.dueDiligenceDeadline, financingType: payload.financingType, homeSaleContingency: payload.homeSaleContingency, homeSaleContingencyDate: payload.homeSaleContingencyDate }, user.uid);
      // Seller deals: recompute closing-date tasks (e.g. Closing Prep to Seller, 7 days before) when the closing date changes.
      if (fresh?.type === 'Seller') await syncTeamTaskDates(id, fresh);
      // First time this deal goes under contract: send the Internal NEW Contract email now (once per deal).
      if (!current?.underContract) await sendUnderContractEmail(id);
      // Weekly check-in tasks for the deal's agent until closed / cancelled / terminated (once per deal).
      await startDealCheckIns(id, owner, user.uid);
      setValues(payload);
      setIsFirstOpen(false);
      navigate(`/transactions/${id}`, { state: { saved: 'Under contract details saved.' } });
    } catch (err) {
      console.error('Could not save under contract form:', err);
      setError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Save as draft: no required fields, no tasks, no emails. The deal page shows "Continue draft".
  const saveDraft = async () => {
    setMsg(null); setError(null); setDraftSaving(true);
    try {
      const draft = {};
      ALL_FIELDS.forEach((f) => { if (isVisible(f, values) && values[f.key] !== undefined) draft[f.key] = values[f.key]; });
      await uploadPendingDocs(owner, id, pendingDocs, user.displayName || user.email || '');
      await update(ref(database, `users/${owner}/transactions/${id}`), { underContractDraft: { ...draft, savedAt: new Date().toISOString() }, updatedAt: new Date().toISOString() });
      navigate(`/transactions/${id}`, { state: { saved: 'Under contract draft saved. No tasks or emails were created.' } });
    } catch (err) {
      console.error('Could not save under contract draft:', err);
      setError('Could not save the draft. Please try again.');
    } finally {
      setDraftSaving(false);
    }
  };

  const renderInput = (f) => {
    if (f.type === 'yesno') {
      const hasError = showErrors && isMissingField(f);
      return (
        <div style={{ display: 'flex', gap: '10px' }}>
          {['Yes', 'No'].map((o) => {
            const active = values[f.key] === o;
            return (
              <button
                key={o}
                type="button"
                onClick={() => setValues((v) => ({ ...v, [f.key]: o, ...(o === 'No' ? { homeSaleContingencyDate: '' } : {}) }))}
                style={{
                  ...yesNoBtn,
                  ...(active ? yesNoBtnActive : {}),
                  ...(hasError && !active ? { borderColor: '#b00020' } : {}),
                }}
              >
                {o}
              </button>
            );
          })}
        </div>
      );
    }

    if (f.type === 'officer') {
      const hasError = showErrors && isMissingField(f);
      return (
        <select
          id={f.key}
          value={values.titleOfficer || ''}
          onChange={(e) => {
            const pick = e.target.value;
            if (!pick) {
              setValues((v) => ({ ...v, titleOfficer: '' }));
            } else if (pick === OTHER_TITLE) {
              setValues((v) => ({ ...v, titleOfficer: OTHER_TITLE, titleCompanyName: '', titleAgentFirstName: '', titleAgentLastName: '', titleAgentEmail: '', titleAgentPhone: '' }));
            } else {
              const o = titleOfficers.find((x) => x.id === pick);
              setValues((v) => ({
                ...v,
                titleOfficer: pick,
                titleCompanyName: o?.company || '',
                titleAgentFirstName: o?.firstName || '',
                titleAgentLastName: o?.lastName || '',
                titleAgentEmail: o?.email || '',
                titleAgentPhone: o?.phone || '',
              }));
            }
          }}
          style={{ ...input, borderColor: hasError ? '#b00020' : '#d8c9a8' }}
        >
          <option value="">Select One</option>
          {titleOfficers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          <option value={OTHER_TITLE}>Other</option>
        </select>
      );
    }

    const common = {
      id: f.key,
      value: values[f.key] || '',
      onChange: (e) => change(f.key, e.target.value),
      style: { ...input, borderColor: showErrors && isMissingField(f) ? '#b00020' : '#d8c9a8' },
    };
    if (f.type === 'select') {
      return (
        <select {...common}>
          <option value="">Select One</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === 'textarea') {
      return <textarea {...common} rows={4} />;
    }
    if (f.type === 'money') {
      return <input {...common} type="text" inputMode="decimal" placeholder="0.00" />;
    }
    if (f.type === 'date' && !f.required) {
      // iOS Safari's date picker has no reliable way to empty the field, so optional dates get a Clear button.
      // onBlur/onInput also catch iOS "Reset", which does not always fire onChange.
      const sync = (e) => { if ((e.target.value || '') !== (values[f.key] || '')) change(f.key, e.target.value || ''); };
      return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input {...common} type="date" onInput={sync} onBlur={sync} style={{ ...common.style, flex: 1, minWidth: 0 }} />
          {values[f.key] ? (
            <button type="button" onClick={() => change(f.key, '')} aria-label={`Clear ${f.label}`}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d8c9a8', background: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>Clear</button>
          ) : null}
        </div>
      );
    }
    return <input {...common} type={f.type || 'text'} />;
  };

  if (loading) {
    return <div style={page}><div style={panel}><p>Loading...</p></div></div>;
  }

  const closeCelebration = () => {
    setShowCelebration(false);
    try { sessionStorage.setItem(`rs-celebrated-${id}`, '1'); } catch (e) { /* storage blocked */ }
  };

  return (
    <div style={page}>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start', maxWidth: 840, margin: '0 auto' }}>
      <div style={{ ...panel, flex: '1 1 420px', margin: 0 }}>
        <button type="button" onClick={() => navigate(`/transactions/${id}`)} style={backBtn}>← Back to transaction</button>
        <button type="button" onClick={() => setShowCelebration(true)} style={{ ...backBtn, float: 'right' }}>Replay celebration</button>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '26px', margin: '0 0 4px', color: '#1a1a1a' }}>Under Contract Form</h1>
        {address && <p style={{ margin: '0 0 20px', color: '#7f7f7f', fontSize: '14px' }}>{address}</p>}

        {SECTIONS.map((s) => {
          const shown = s.fields.filter((f) => isVisible(f, values));
          if (shown.length === 0) return null;
          return (
            <section key={s.title} style={sectionWrap}>
              <div style={sectionTitle}>{s.title}</div>
              {shown.map((f) => (
                <div key={f.key} style={field}>
                  <label style={label} htmlFor={f.key}>
                    {f.label} {f.required && <span style={{ color: '#b00020' }}>*</span>}
                  </label>
                  {f.days ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 200px', minWidth: 0 }}>{renderInput(f)}</div>
                      <input type="number" min="0" inputMode="numeric" aria-label={`${f.label}: days after Effective Date`} placeholder="days"
                        value={values[`${f.key}Days`] ?? ''} onChange={(e) => changeDays(f.key, e.target.value.replace(/\D/g, ''))}
                        style={{ ...input, width: 80, flex: '0 0 80px' }} />
                      <select aria-label={`${f.label}: day type`} value={values[`${f.key}DayMode`] || 'calendar'} onChange={(e) => changeDays(f.key, values[`${f.key}Days`] ?? '', e.target.value)}
                        style={{ ...input, width: 'auto', flex: '0 0 auto' }}>
                        <option value="calendar">calendar days</option>
                        <option value="business">business days</option>
                      </select>
                    </div>
                  ) : renderInput(f)}
                  {f.days && values[`${f.key}Days`] !== undefined && values[`${f.key}Days`] !== '' && (() => {
                    const r = deadlineFrom(values.underContractDate, values[`${f.key}Days`], values[`${f.key}DayMode`] || 'calendar');
                    if (!values.underContractDate) return <div style={hint}>Enter the Under Contract (Effective) Date to count days.</div>;
                    return r ? <div style={hint}>{values[`${f.key}Days`]} {values[`${f.key}DayMode`] === 'business' ? 'business' : 'calendar'} days after the Effective Date (day 0){r.rolled ? `; ${r.landedOn} is a ${r.reason}, so it moves to the next business day` : ''}.</div> : null;
                  })()}
                  {f.hint && <div style={hint}>{f.hint}</div>}
                  {showErrors && isMissingField(f) && <div style={errorText}>Required</div>}
                </div>
              ))}
            </section>
          );
        })}

        <DocChecklistUpload title={`${/seller/i.test(txType) ? 'Seller' : 'Buyer'} under contract documents`} docs={requiredDocs} pending={pendingDocs} onChange={setPendingDocs} existing={documents} showErrors={showErrors} />

        <section style={sectionWrap}>
          <div style={sectionTitle}>Executed Contract Documents</div>
          <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#5B6270' }}>Upload the final signed contract and related documents. Tina attaches these to the title email.</p>
          {documents.length === 0 && <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#7f7f7f' }}>No documents uploaded yet.</p>}
          {documents.map((d) => (
            <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #EEE' }}>
              <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', color: '#1a1a1a' }}>{d.name}</a>
              <button type="button" onClick={() => removeDocument(d)} style={{ ...backBtn, color: '#b00020' }}>Remove</button>
            </div>
          ))}
          <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" disabled={uploading} onChange={(e) => uploadDocuments(e.target.files)} style={{ marginTop: 8, fontSize: '14px' }} />
          {uploading && <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#5B6270' }}>Uploading...</p>}
        </section>

        {error && <p style={{ color: '#b00020', fontSize: '14px' }}>{error}</p>}
        {msg && <p style={{ color: '#2e7d32', fontSize: '14px' }}>{msg}</p>}
        <button type="button" onClick={save} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving...' : 'Save Under Contract'}
        </button>
        <button type="button" onClick={saveDraft} disabled={saving || draftSaving} style={{ ...primaryBtn, marginTop: 10, background: '#ffffff', color: '#1a1a1a', border: '1px solid #1a1a1a', opacity: draftSaving ? 0.6 : 1 }}>
          {draftSaving ? 'Saving draft...' : 'Save as draft'}
        </button>
      </div>
      {showCelebration && <CelebrationOverlay onClose={closeCelebration} />}
      </div>
    </div>
  );
}
